import { createContext, type ReactNode, useContext, useMemo } from 'react';

import { kitMessages } from './messages/es-co';
import type { KitMessageKey, MessageParams, Messages, TranslateOf } from './types';

/**
 * Builds a translate function over a catalogue. A missing key renders as the key itself, so a
 * screen never shows empty text and the gap is visible.
 *
 * @param {Messages} messages -- The catalogue.
 * @returns The translate function.
 */
export const createTranslate =
  (messages: Messages): TranslateOf<string> =>
  (key, params) => {
    const template = messages[key] ?? key;
    return params
      ? template.replace(/\{(\w+)\}/g, (match, name: string) =>
          name in params ? String(params[name]) : match,
        )
      : template;
  };

const I18nContext = createContext<TranslateOf<string>>(createTranslate(kitMessages));

/**
 * Provides the translate function of a catalogue (an app's merged messages).
 */
export const I18nProvider = ({
  messages,
  children,
}: {
  messages: Messages;
  children: ReactNode;
}) => {
  const translate = useMemo(() => createTranslate(messages), [messages]);
  return <I18nContext.Provider value={translate}>{children}</I18nContext.Provider>;
};

/**
 * The translate function, typed by the caller's keys. Apps wrap it once with their own key type.
 *
 * @returns `t(key, params)`.
 */
export const useTranslate = <K extends string = string>(): TranslateOf<K> =>
  useContext(I18nContext) as TranslateOf<K>;

/**
 * The translate function typed by the kit's own keys, for the kit's screens.
 *
 * @returns `t(key, params)`.
 */
export const useKitT = (): TranslateOf<KitMessageKey> => useTranslate<KitMessageKey>();

export { kitMessages } from './messages/es-co';
export type { MessageParams };
